import {
    IAddCard,
    IConfirmMisson,
    IDeleteCard,
    IGetAmphur,
    IGetCardHistory,
    IGetCountrySetting,
    IGetLuckydrawList,
    IGetMission,
    IGetSubDistrict,
    ILinkCard,
    IMemberType,
    IPaymentTopUp,
    IRedeemLuckyDraw,
    ITransferCard,
    IUnlinkCard,
    IUploadReceipt,
} from "@interfaces";
import { getPrefix } from "@Rocket-Innovation/components/utilities/getPrefix";
import axiosClient from "./axiosClient";
import axiosClientV2 from "./axiosClientV2";
import axiosClientV2WithToken from "./axiosClientV2WithToken";
import axiosPlainClient from "./axiosPlain";

// Simple in-module cache to deduplicate getAllProvince requests
let _cacheAllProvince: any = null;
let _allProvincePromise: Promise<any> | null = null;

const undefinedPathSegmentPattern = /(^|\/)undefined(?=\/|$|\?)/;
const skipUndefinedPathRequest = (url: string) => {
    if (!undefinedPathSegmentPattern.test(url)) return null;

    return Promise.reject(new Error(`Skipped API call because url contains undefined path: ${url}`));
};

export const merchantApi = {
    getTrafficSourceId: (id: string) => {
        const url = `/traffic-source/${id}/qrcode`;
        return axiosClient.get(url);
    },
    getCouponId: (id: string) => {
        const url = `/coupons/${id}/qrcode`;
        return axiosClient.get(url);
    },
    getPersonalCoupon: (pId: string) => {
        const url = `/histories/${pId}/history`;
        return axiosClient.get(url);
    },
    getFreePointId: (id: string) => {
        const url = `/free-points/${id}/qrcode`;
        return axiosClient.get(url);
    },
    getMerchantConfig: () => {
        const url = "/auth/merchant-config";
        return axiosClient.get(url);
    },
    getMerchantConfigPrefix: () => {
        const url = "/auth/getMerchantConfigPrefix";
        return axiosClient.get(url);
    },
    getThemeMode: (mer_code: string) => {
        const url = "/auth/getMerchantConfigPrefix";
        const config = {
            headers: {
                "merchant-prefix": mer_code,
            },
        };
        return axiosClient.get(url, config);
    },
    getInitMerchant: (merchantId: string) => {
        const url = "auth/merchant-config";
        const config = {
            headers: {
                "merchant-id": merchantId,
            },
        };
        return axiosClient.get(url, config);
    },
    uploadImage: (params: FormData) => {
        const url = "/upload";
        return axiosClient.post(url, params, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    getMerchantTermCondition: () => {
        const url = "merchants/getMerchantTermCondition";
        return axiosClient.get(url);
    },
    uploadReceipt: (value: IUploadReceipt) => {
        const url = "/approvereceipt/upload";
        return axiosClient.post(url, value);
    },
    getCardList: (value: string) => {
        const url = `/cardMember/list/${value}`;
        return axiosClient.get(url);
    },
    createCardAuto: () => {
        const url = "/cardMember/regis/auto";
        return axiosClient.post(url);
    },
    addCard: (params: IAddCard) => {
        const url = "/cardMember/regis";
        return axiosClient.post(url, params);
    },
    linkCard: (params: ILinkCard) => {
        const url = "/cardMember/linkPhysicalCard";
        return axiosClient.post(url, params);
    },
    unlinkCard: (params: IUnlinkCard) => {
        const url = "/cardMember/unlink-physical-card";
        return axiosClient.post(url, params);
    },
    cardTransfer: (params: ITransferCard) => {
        const url = "/cardMember/transfer";
        return axiosClient.post(url, params);
    },
    getCardHistory: (params: IGetCardHistory) => {
        const url = "product_order/getCardTransactionByClientId";
        return axiosPlainClient.post(url, params);
    },
    deleteCard: (params: IDeleteCard) => {
        const url = `/cardMember/remove/${params?.cardNum}/${params?.cardId}`;
        return axiosClient.delete(url);
    },
    getAllProvince: () => {
        const url = "geos/getAllProvince";
        // Return cached result if present
        if (_cacheAllProvince) {
            return Promise.resolve(_cacheAllProvince);
        }
        // If a request is already in-flight, return the same promise
        if (_allProvincePromise) {
            return _allProvincePromise;
        }

        _allProvincePromise = axiosPlainClient
            .post(url)
            .then((res) => {
                _cacheAllProvince = res;
                _allProvincePromise = null;
                return res;
            })
            .catch((err) => {
                _allProvincePromise = null;
                throw err;
            });

        return _allProvincePromise;
    },
    getAmphur: (params: IGetAmphur) => {
        const url = "geos/getAllAmphurByProvinceId";
        return axiosPlainClient.post(url, params);
    },
    getSubDistrict: (params: IGetSubDistrict) => {
        const url = "geos/getAllDistrictByAmphurId";
        return axiosPlainClient.post(url, params);
    },
    getAddressIsRequire: () => {
        const url = "/auth/getAddressIsRequire";
        return axiosClient.get(url);
    },
    getCountrySetting: (params: IGetCountrySetting) => {
        const url = "/merchants/getCountrySetting";
        return axiosClient.post(url, params);
    },
    getMemberType: () => {
        const url = "/merchants/getMemberType";
        return axiosClient.get(url);
    },
    getNewsList: () => {
        const url = "/news";
        return axiosClient.get(url);
    },
    postMemberType: (params: IMemberType) => {
        const url = "/memberTypeHistory/upload";
        return axiosClient.post(url, params);
    },
    getSurveyList: () => {
        const url = "/contacts/survey";
        return axiosClient.post(url);
    },
    getSurveyDetails: (params: string) => {
        const url = "/contacts/client/survey/";
        return axiosClient.get(url + params);
    },
    getInviteLink: () => {
        const url = "/invite-links/friend";
        return axiosClient.post(url);
    },
    getInviteLinkSeller: () => {
        const url = "/invite-links/seller";
        return axiosClient.post(url);
    },
    getEndYearPoint: () => {
        const url = "/points/getClientPointExpireEndYear";
        return axiosClient.post(url);
    },
    sendSurveyAns: (payload: any) => {
        const url = "/contacts/save-survey";
        return axiosClient.post(url, payload);
    },
    getMission: (params: IGetMission) => {
        const url = "pos_mission/clientGetPosProfileMission";
        return axiosPlainClient.get(url, { params });
    },
    confirmReward: (params: IConfirmMisson) => {
        const url = `/lucky-draw/${params.id}/${params.pos_profile_mission_id}/${params?.profile_id}/mission-redeemed`;
        return axiosClient.post(url, {
            shippingAddress: params.shippingAddress,
        });
    },
    getLuckyDrawList: (params: IGetLuckydrawList) => {
        const url = "/lucky-draw/client";
        return axiosClient.get(url, { params });
    },
    redeemLuckyDraw: (params: IRedeemLuckyDraw) => {
        const url = "/lucky-draw/redeemed";
        return axiosClient.post(url, params);
    },
    qrClaimPoint: (params: any) => {
        const url = "/auth/clientAppScanPosReceiptClaimPoint";
        return axiosClient.post(url, params);
    },
    getIsFullName: () => {
        const url = "/merchants/admin-config-fullname";
        return axiosClient.get(url);
    },
    paymentTopUp: (params: IPaymentTopUp) => {
        const url = "cardMember/send-to-pay-solution";
        return axiosClient.post(url, params);
    },

    getRewardDetails: (params: string) => {
        const url = "rewards/";
        return axiosClient.get(url + params);
    },
    getMerchantNew: () => {
        const prefix = getPrefix();
        const url = `merchants/${prefix}`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getUploadReceiptSetting: (params: string) => {
        const url = `merchants/${params}/upload-receipt-setting`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getMissionSetting: (params: string) => {
        const url = `merchants/${params}/mission`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2WithToken.get(url);
    },
    getTermAndCondition: (params: string) => {
        const url = `merchants/${params}/term-condition`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getMemberShipTiers: (params: string) => {
        const url = `merchants/${params}/membership-tiers`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getAdvanceSettings: (params: string) => {
        const url = `merchants/${params}/advance-setting`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getSignUpSettings: (params: string) => {
        const url = `merchants/${params}/signup-settings`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getLuckyDrawSettings: (params: string) => {
        const url = `merchants/${params}/lucky-draw-display-setting`;
        const skippedRequest = skipUndefinedPathRequest(url);
        if (skippedRequest) return skippedRequest;

        return axiosClientV2.get(url);
    },
    getFormQuestions: (params: any) => {
        const url = `auth/getFormQuestions`;
        return axiosClient.post(url, params);
    },
};
